import { AxiosError } from 'axios';
import { HTTP_STATUS_CODES } from 'Enum/HttpStatusCodes';
import { addImposterToUrl } from 'Utils';
import { api } from '../axios';
import {
  ICreateReferralPayload,
  ICreateReferralResult,
  ISelfReferralEligibilityResult,
  ISelfReferralFileResponse,
  ISelfServiceReferralsResponse,
} from 'Interfaces/SelfServiceReferrals';

const dataBaseURL = '/SelfServiceReferrals';
const generationBaseURL = '/SelfReferralGeneration';

const isCreateReferralFailure = (
  data: unknown,
): data is ICreateReferralResult => {
  if (!data || typeof data !== 'object') return false;
  const candidate = data as { isSuccess?: boolean; errorId?: unknown };
  return candidate.isSuccess === false && typeof candidate.errorId === 'number';
};

export const getCreateReferralFailure = (
  error: unknown,
): ICreateReferralResult | null => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return null;
  }
  const axiosError = error as AxiosError<unknown>;
  if (axiosError.response?.status !== HTTP_STATUS_CODES.BadRequest) {
    return null;
  }
  const data = axiosError.response.data;
  return isCreateReferralFailure(data) ? data : null;
};

export const getSelfServiceReferralsData = async (): Promise<
  ISelfServiceReferralsResponse | undefined
> => {
  const result = await api.get<ISelfServiceReferralsResponse>(
    addImposterToUrl(`${dataBaseURL}/data`, true),
  );
  if (!result?.data) return undefined;

  return result.data;
};

const omitMedicalSectorIdWhenInvalid = (
  payload: ICreateReferralPayload,
): Omit<ICreateReferralPayload, 'medicalSectorId'> & {
  medicalSectorId?: number;
} => {
  const { medicalSectorId, ...rest } = payload;
  if (medicalSectorId != null && medicalSectorId > 0) {
    return { ...rest, medicalSectorId };
  }
  return rest;
};

export const createReferral = async (
  payload: ICreateReferralPayload,
): Promise<ICreateReferralResult | undefined> => {
  try {
    const result = await api.post<ICreateReferralResult>(
      addImposterToUrl(generationBaseURL, true),
      omitMedicalSectorIdWhenInvalid(payload),
    );
    if (result?.data) return result.data;
  } catch (error) {
    const createFailure = getCreateReferralFailure(error);
    if (createFailure) return createFailure;
    throw error;
  }
};

export const getSelfReferralFile = async (
  selfReferralId: number,
): Promise<ISelfReferralFileResponse | undefined> => {
  const result = await api.get<ISelfReferralFileResponse>(
    addImposterToUrl(`${generationBaseURL}/${selfReferralId}/file`, true),
  );
  if (result?.data) return result.data;
};

export const checkSelfReferralEligibility = async (
  isrId: string,
  paramsTableValue: number,
  paramsTableKey: string,
  sprCode: string,
  medicalSectorId?: number,
): Promise<ISelfReferralEligibilityResult | undefined> => {
  const params = new URLSearchParams({
    paramsTableValue: String(paramsTableValue),
    paramsTableKey,
    isrId,
    sprCode,
  });
  if (medicalSectorId !== undefined && medicalSectorId > 0) {
    params.set('medicalSectorId', String(medicalSectorId));
  }

  const res = await api.get<ISelfReferralEligibilityResult>(
    `${dataBaseURL}/eligibility?${params.toString()}`,
  );
  return res.data;
};

export const isEligibleForSelfReferral = async (
  isrId: string,
  paramsTableValue: number,
  paramsTableKey: string,
  sprCode: string,
  medicalSectorId?: number,
): Promise<boolean> => {
  const result = await checkSelfReferralEligibility(
    isrId,
    paramsTableValue,
    paramsTableKey,
    sprCode,
    medicalSectorId,
  );
  return result?.isSuccess === true;
};
